import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import { Box, Typography, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";

// Custom components
import GlassCard from "components/GlassCard";
import GlassCardLoader from "components/GlassCard/GlassCardLoader";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import CircularAIIcon from "components/CircularAIIcon";
import TypingText from "components/TypingText";

// Design tokens
import { ACCENT_COLORS, GRADIENTS } from "utils/designTokens";

// Services
import { productShowdownService } from "services/productShowdownService";

// Context
import { useAnalytics } from "context/AnalyticsContext";
import { analyticsService } from "services/trackingService";

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const ColumnHeader = styled(Box)(({ accent }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  padding: "8px 20px",
  borderRadius: "12px",
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${accent}33`,
  marginBottom: "12px",
}));

// ---------------------------------------------------------------------------
// Reusable content primitives
// ---------------------------------------------------------------------------

const ContentLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: "rgba(255,255,255,0.45)",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontWeight: 600,
      fontSize: "0.7rem",
      mb: 0.5,
      display: "block",
    }}
  >
    {children}
  </Typography>
);

const ContentText = ({ children, sx }) => (
  <Typography
    variant="body2"
    sx={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.7, fontSize: "0.875rem", ...sx }}
  >
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
    {(items || []).map((item, idx) => (
      <Box
        component="li"
        key={idx}
        sx={{
          color: "rgba(255,255,255,0.85)",
          fontSize: "0.875rem",
          lineHeight: 1.8,
          "::marker": { color: "rgba(255,255,255,0.35)" },
        }}
      >
        {item}
      </Box>
    ))}
  </Box>
);

const { primary: ACCENT_OURS, secondary: ACCENT_THEIRS } = ACCENT_COLORS;

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function ProductComparisonPage() {
  const {
    showdownAnchor, setShowdownAnchor,
    showdownCompetitor, setShowdownCompetitor,
    setShowdownAnchors, setShowdownCompetitors,
    showdownCompetitors,
  } = useAnalytics();

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Derive company name for the competitor column header from the current list
  const selectedCompetitorEntry = showdownCompetitors.find(c => c.id === showdownCompetitor);
  const competitorCompany = selectedCompetitorEntry?.companyName || "";

  // ---------------------------------------------------------------------------
  // Bootstrap: fetch anchor list, push into context, set default
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    productShowdownService.getAnchors()
      .then(res => {
        if (cancelled) return;
        const anchors = (res.data || []).map(a => ({ id: a.productFamily, name: a.productFamily }));
        setShowdownAnchors(anchors);
        // Default to first anchor only if nothing is selected yet
        setShowdownAnchor(prev => prev || (anchors[0]?.id ?? null));
      })
      .catch(e => console.error("Failed to fetch Product Showdown anchors:", e));

    return () => {
      cancelled = true;
      setShowdownAnchors([]);
      setShowdownCompetitors([]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // When anchor changes: fetch competitors, pick default, then fetch comparison.
  // All in one coordinated effect so competitor is always valid before we call
  // getComparison — no window where anchor is new but competitor is stale.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!showdownAnchor) return;
    let cancelled = false;

    const run = async () => {
      // 1. Reset UI state before anything async completes
      setComparison(null);
      setError(null);
      setShowdownCompetitor(null);
      setShowdownCompetitors([]);

      // 2. Fetch competitors for this anchor
      let competitors = [];
      try {
        const res = await productShowdownService.getCompetitorsForAnchor(showdownAnchor);
        if (cancelled) return;
        competitors = (res.data || []).map(c => ({
            id: c.productFamily,
            name: `${c.productFamily} (${c.companyName})`,
            companyName: c.companyName,
          }));
        setShowdownCompetitors(competitors);
      } catch (e) {
        if (cancelled) return;
        console.error("Failed to fetch competitors for anchor:", e);
        return; // No competitors → nothing to compare
      }

      if (!competitors.length) return;

      // 3. Pick the default competitor
      const defaultCompetitor = competitors[0].id;
      setShowdownCompetitor(defaultCompetitor);

      // 4. Fetch comparison for anchor + default competitor
      setLoading(true);
      try {
        const res = await productShowdownService.getComparison(showdownAnchor, defaultCompetitor);
        if (cancelled) return;
        setComparison(res.data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 503
          ? "This comparison is not available yet. Please check back soon."
          : "Failed to generate comparison. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [showdownAnchor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // When competitor selector changes (user picks a different competitor manually)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!showdownAnchor || !showdownCompetitor) return;

    analyticsService.trackPageView("PRODUCT_SHOWDOWN", {
      competitorId: showdownCompetitor,
      anchorId: showdownAnchor,
    });
  }, [showdownCompetitor, showdownAnchor]);

  useEffect(() => {
    if (!showdownAnchor || !showdownCompetitor) return;

    let cancelled = false;

    setComparison(null);
    setError(null);
    setLoading(true);

    productShowdownService.getComparison(showdownAnchor, showdownCompetitor)
      .then(res => {
        if (cancelled) return;
        setComparison(res.data);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 503
          ? "This comparison is not available yet. Please check back soon."
          : "Failed to generate comparison. Please try again.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [showdownCompetitor]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = comparison?.comparison_data;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={3}>

        {/* ---- Generating state ---- */}
        {loading && (
          <VuiBox>
            <VuiBox display="flex" alignItems="flex-start" mb={4}>
              <CircularAIIcon size="50px" />
              <VuiBox ml={2}>
                <TypingText
                  text="Analysing latest updates & regulatory documents of competing products…"
                  speed={18}
                  sx={{
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    lineHeight: 1.4,
                    background: GRADIENTS.text,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                />
              </VuiBox>
            </VuiBox>
            <Grid container spacing={3}>
              {[...Array(6)].map((_, i) => (
                <Grid item xs={12} md={i < 4 ? 6 : 12} key={i}>
                  <GlassCard title=""><GlassCardLoader /></GlassCard>
                </Grid>
              ))}
            </Grid>
          </VuiBox>
        )}

        {/* ---- Error state ---- */}
        {!loading && error && (
          <StandardMessageDisplay
            type="info"
            title="No Data Available"
            message={error}
            containerHeight="400px"
          />
        )}

        {/* ---- No pair selected ---- */}
        {!loading && !error && !showdownAnchor && (
          <StandardMessageDisplay
            type="noData"
            title="Select a product"
            message="Choose your product and a competitor from the selectors above."
            containerHeight="400px"
          />
        )}

        {/* ---- Comparison content ---- */}
        {!loading && !error && data && (
          <Grid container spacing={3}>

            {/* Column headers */}
            <Grid item xs={12} md={6}>
              <ColumnHeader accent={ACCENT_OURS}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", background: ACCENT_OURS }} />
                <VuiTypography variant="caption" fontWeight="bold"
                  sx={{ color: "white", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "1rem" }}>
                  Terumo Neuro
                </VuiTypography>
              </ColumnHeader>
            </Grid>
            <Grid item xs={12} md={6}>
              <ColumnHeader accent={ACCENT_THEIRS}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", background: ACCENT_THEIRS }} />
                <VuiTypography variant="caption" fontWeight="bold"
                  sx={{ color: "white", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "1rem" }}>
                  {competitorCompany || showdownCompetitor || "Competitor"}
                </VuiTypography>
              </ColumnHeader>
            </Grid>

            {/* ROW 1 — Overview */}
            <Grid item xs={12} md={6}>
              <GlassCard title={data.overview?.our_product?.name || "Our Product"}>
                <ContentText>{data.overview?.our_product?.summary}</ContentText>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassCard title={data.overview?.competitor_product?.name || "Competitor Product"}>
                <ContentText>{data.overview?.competitor_product?.summary}</ContentText>
              </GlassCard>
            </Grid>

            {/* ROW 2 — USP */}
            <Grid item xs={12} md={6}>
              <GlassCard title="Our Unique Selling Point">
                <ContentLabel>Headline</ContentLabel>
                <ContentText sx={{ fontWeight: 600, mb: 1.5 }}>{data.usp?.our_silver_bullet?.headline}</ContentText>
                <ContentLabel>Key Benefits</ContentLabel>
                <BulletList items={data.usp?.our_silver_bullet?.key_benefits} />
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassCard title="Opposing USP Claim">
                <ContentLabel>Headline</ContentLabel>
                <ContentText sx={{ fontWeight: 600, mb: 1.5 }}>{data.usp?.their_main_claim?.headline}</ContentText>
                <ContentLabel>Key Specs</ContentLabel>
                <BulletList items={data.usp?.their_main_claim?.key_specs} />
              </GlassCard>
            </Grid>

            {/* ROW 3 — Vulnerabilities */}
            <Grid item xs={12} md={6}>
              <GlassCard title="Our Vulnerability">
                <ContentLabel>Known Weakness</ContentLabel>
                <ContentText sx={{ mb: 2 }}>{data.weakness?.our_vulnerability?.flaw}</ContentText>
                <ContentLabel>Objection Handling Script</ContentLabel>
                <ContentText sx={{ fontStyle: "italic", borderLeft: `3px solid ${ACCENT_OURS}44`, pl: 2, py: 0.5 }}>
                  {data.weakness?.our_vulnerability?.objection_handling}
                </ContentText>
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassCard title="Their Vulnerability">
                <ContentLabel>Known Weakness</ContentLabel>
                <ContentText sx={{ mb: 2 }}>{data.weakness?.their_vulnerability?.flaw}</ContentText>
                <ContentLabel>Reality Check</ContentLabel>
                <ContentText sx={{ fontStyle: "italic", borderLeft: `3px solid ${ACCENT_THEIRS}44`, pl: 2, py: 0.5 }}>
                  {data.weakness?.their_vulnerability?.reality_check}
                </ContentText>
              </GlassCard>
            </Grid>

            {/* ROW 4 — Marketing Playbook */}
            <Grid item xs={12}>
              <GlassCard title="Marketing Playbook">
                <Box mb={3}>
                  <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 0.5 }}>
                    1. Core Messaging Matrix
                  </VuiTypography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", mb: 2 }}>
                    {data.marketing_playbook?.core_messaging_matrix?.description}
                  </Typography>
                  <Grid container spacing={3}>
                    {(data.marketing_playbook?.core_messaging_matrix?.items || []).map((item, idx) => (
                      <Grid item xs={12} md={6} key={idx}>
                        <Box sx={{ p: 2, borderRadius: "12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", height: "100%" }}>
                          <ContentLabel>Target Audience: {item.target_audience}</ContentLabel>
                          <ContentText sx={{ mb: 1.5 }}>
                            <strong>Pain Point:</strong> {item.primary_pain_point}<br />
                            <strong>Solution:</strong> {item.solution}
                          </ContentText>
                          <ContentText sx={{ fontStyle: "italic", borderLeft: `3px solid ${ACCENT_OURS}44`, pl: 2, py: 0.5 }}>
                            {item.core_hook}
                          </ContentText>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 1.5 }}>2. Key Marketing Campaigns</VuiTypography>
                    {(data.marketing_playbook?.key_campaigns || []).map((camp, idx) => (
                      <Box key={idx} mb={2}><ContentLabel>{camp.name}</ContentLabel><ContentText>{camp.desc}</ContentText></Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 1.5 }}>3. Sales Enablement Deliverables</VuiTypography>
                    {(data.marketing_playbook?.sales_enablement || []).map((tool, idx) => (
                      <Box key={idx} mb={2}><ContentLabel>{tool.name}</ContentLabel><ContentText>{tool.desc}</ContentText></Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 1.5 }}>4. Congress & Event Strategy</VuiTypography>
                    {(data.marketing_playbook?.event_strategy || []).map((strat, idx) => (
                      <Box key={idx} mb={2}><ContentLabel>{strat.name}</ContentLabel><ContentText>{strat.desc}</ContentText></Box>
                    ))}
                  </Grid>
                </Grid>
              </GlassCard>
            </Grid>

            {/* ROW 5 — Sales Playbook */}
            <Grid item xs={12}>
              <GlassCard title="Sales Playbook">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 0.5 }}>Consultative Discovery</VuiTypography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", mb: 2 }}>{data.recommendations?.consultative_discovery?.description}</Typography>
                    <ContentLabel>What to Ask</ContentLabel>
                    <ContentText sx={{ fontStyle: "italic", borderLeft: `3px solid ${ACCENT_OURS}44`, pl: 2, py: 0.5 }}>
                      {data.recommendations?.consultative_discovery?.what_to_ask}
                    </ContentText>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 0.5 }}>Case Coverage Strategy</VuiTypography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", mb: 2 }}>{data.recommendations?.case_coverage_strategy?.description}</Typography>
                    <ContentLabel>Drive Immediate Value</ContentLabel>
                    <ContentText sx={{ mb: 2 }}>{data.recommendations?.case_coverage_strategy?.drive_immediate_value}</ContentText>
                    <ContentLabel>Contrast the Competition</ContentLabel>
                    <ContentText>{data.recommendations?.case_coverage_strategy?.contrast_the_competition}</ContentText>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <VuiTypography variant="h6" fontWeight="bold" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", mb: 0.5 }}>VAC Positioning</VuiTypography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", mb: 2 }}>{data.recommendations?.vac_positioning?.description}</Typography>
                    <ContentLabel>The Anchor</ContentLabel>
                    <ContentText sx={{ mb: 2 }}>{data.recommendations?.vac_positioning?.the_anchor}</ContentText>
                    <ContentLabel>The Economic Pitch</ContentLabel>
                    <ContentText>{data.recommendations?.vac_positioning?.the_economic_pitch}</ContentText>
                  </Grid>
                </Grid>
              </GlassCard>
            </Grid>

          </Grid>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default ProductComparisonPage;
